import { Button, Flex, Form, PasswordField, PasswordValidation, Text, toast } from '@approvalmax/ui/src/components';
import { hooks } from '@approvalmax/utils';
import { zodResolver } from '@hookform/resolvers/zod';
import { FC, memo } from 'react';
import { useForm } from 'react-hook-form';
import { useHistory } from 'react-router-dom';
import { TwoFaActionWizard } from 'shared/components';
import { useFinishResetPassword } from 'shared/data/webApp/v1';
import { getPath, Path } from 'urlBuilder';
import { z } from 'zod';

import { passwordFieldName } from './Content.constants';
import { useTwoFa } from './Content.hooks';
import { messages } from './Content.messages';
import { schema } from './Content.schema';
import { ContentProps } from './Content.types';

export const Content: FC<ContentProps> = memo((props) => {
    const {
        startData: { stateId, isTFAAlternativeEmailEnabled, isTFABackupCodesEnabled, requiresTFA },
    } = props;

    const history = useHistory();
    const [postAction] = hooks.useQueryParam('postAction');

    const { mutate, isLoading } = useFinishResetPassword({
        onSuccess: () => {
            toast.success(messages.successMessage);
            history.replace(getPath(Path.login, { redirectPath: postAction }));
        },
    });

    const form = useForm<z.infer<typeof schema>>({
        resolver: zodResolver(schema),
    });

    const password = form.watch(passwordFieldName);

    const onSubmit = form.handleSubmit((data) => {
        mutate({ body: { stateId, newPassword: data.password } });
    });

    const { isTFALoading, isTFAPassed, handleCloseTFA, handleVerifyTFA, handleRequestCodeOnEmail } = useTwoFa({
        stateId,
        requiresTFA,
    });

    if (!isTFAPassed) {
        return (
            <TwoFaActionWizard
                open={!isTFAPassed}
                title={messages.title}
                isLoading={isTFALoading}
                onEnterAppCode={handleVerifyTFA}
                onEnterBackupCode={handleVerifyTFA}
                onEnterEmailCode={handleVerifyTFA}
                onSendCodeViaEmail={handleRequestCodeOnEmail}
                isTFAAlternativeEmailEnabled={Boolean(isTFAAlternativeEmailEnabled)}
                isTFABackupCodesEnabled={Boolean(isTFABackupCodesEnabled)}
                onClose={handleCloseTFA}
                disableAutoClose
            />
        );
    }

    return (
        <Form form={form} onSubmit={onSubmit}>
            <Flex direction='column' spacing='24'>
                <Text font='headline' fontSize='small' fontWeight='medium' as='h1' textAlign='center'>
                    {messages.title}
                </Text>

                <PasswordField
                    name={passwordFieldName}
                    label={messages.passwordLabel}
                    placeholder={messages.passwordPlaceholder}
                    required
                    initFocus
                    clearable={false}
                />

                <PasswordValidation value={password} invalid={!form.formState.isValid && form.formState.isSubmitted} />

                <Button type='submit' color='green80' size='large' block progress={isLoading}>
                    {messages.setPasswordButton}
                </Button>
            </Flex>
        </Form>
    );
});

Content.displayName = 'Content';
